import { useEffect, useMemo, useRef, useState } from 'react';
import MapPicker from './components/MapPicker';
import PropertyForm, { type FormValues } from './components/PropertyForm';
import ResultPanel from './components/ResultPanel';
import ComplianceModal from './components/ComplianceModal';
import HistoryPanel from './components/HistoryPanel';
import ExploreLinks from './components/ExploreLinks';
import SellerTools from './components/SellerTools';
import { ApiError, predict } from './api/client';
import type { PredictResponse } from './types';
import { parseDeepLink, buildDeepLinkParams, buildShareUrl } from './lib/deepLink';
import { track } from './lib/analytics';
import { type SavedEntry, loadHistory, pushHistory } from './lib/storage';
import { splitYen } from './lib/format';
// The site chrome is defined once, in the module the static generators also
// use, so the header cannot drift between the estimator and the ~1,100
// generated pages. See scripts/lib/shell.mjs.
// @ts-expect-error -- plain .mjs with no type declarations
import { HEADER, NAV as SITE_NAV, C as SHELL } from '../scripts/lib/shell.mjs';

interface LastInput {
  values: FormValues;
  position: [number, number];
}

/** Header cross-links, from the shared chrome, with the analytics label this
 *  side needs attached. */
const NAV: { href: string; label: string; target: string }[] = (
  SITE_NAV as { href: string; label: string }[]
).map((n) => ({ ...n, target: n.href.replace(/^\/|\.html$/g, '') || 'home' }));

export default function App() {
  const initial = useMemo(
    () => (typeof window !== 'undefined' ? parseDeepLink(window.location.search) : null),
    [],
  );

  const [position, setPosition] = useState<[number, number] | null>(initial?.position ?? null);
  const [result, setResult] = useState<PredictResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [lastInput, setLastInput] = useState<LastInput | null>(null);
  const [history, setHistory] = useState<SavedEntry[]>([]);

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  // On mobile the report column renders below the map, often out of view
  // after pressing 推定する. Auto-scroll the result into view as soon as a
  // new prediction (or error) lands so the answer doesn't have to be hunted
  // for. Skipped on viewports ≥ 768 px where the report is a side column and
  // already visible.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!result && !error) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) return;
    if (!resultRef.current) return;
    // Slight delay so the new content is in the DOM before we scroll to it.
    const t = window.setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => window.clearTimeout(t);
  }, [result, error]);

  const formInitial: Partial<FormValues> | undefined = initial
    ? {
        propertyType: initial.propertyType,
        floorPlan: initial.floorPlan,
        buildingYear: initial.buildingYear,
        area: initial.area,
        floorArea: initial.floorArea,
        roadWidthM: initial.roadWidthM,
        roadDirection: initial.roadDirection,
      }
    : undefined;

  const handleSubmit = async (values: FormValues) => {
    if (!position) return;
    setLoading(true);
    setError(null);
    setResult(null);
    track('predict_submit', { propertyType: values.propertyType, floorPlan: values.floorPlan });

    const params = buildDeepLinkParams({
      position,
      propertyType: values.propertyType,
      floorPlan: values.floorPlan,
      buildingYear: values.buildingYear,
      area: values.area,
      floorArea: values.floorArea,
      roadWidthM: values.roadWidthM,
      roadDirection: values.roadDirection,
    });
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    }

    try {
      const res = await predict({
        lat: position[0],
        lng: position[1],
        propertyType: values.propertyType,
        floorPlan: values.floorPlan,
        buildingYear: values.buildingYear,
        area: values.area,
        floorArea: values.floorArea,
        roadWidthM: values.roadWidthM,
        roadDirection: values.roadDirection,
      });
      setResult(res);
      setLastInput({ values, position });
      const entry: SavedEntry = {
        ts: Date.now(),
        position,
        values,
        result: {
          predictedPrice: res.predictedPrice,
          priceRange: res.priceRange,
          municipalityName: res.location.municipalityName,
          districtName: res.location.districtName,
          nearestStation: res.location.nearbyStations[0] ?? null,
        },
      };
      setHistory(pushHistory(entry));
      track('predict_success');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setError(msg);
      track('predict_error', { message: msg });
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = lastInput
    ? buildShareUrl({
        position: lastInput.position,
        propertyType: lastInput.values.propertyType,
        floorPlan: lastInput.values.floorPlan,
        buildingYear: lastInput.values.buildingYear,
        area: lastInput.values.area,
        floorArea: lastInput.values.floorArea,
        roadWidthM: lastInput.values.roadWidthM,
        roadDirection: lastInput.values.roadDirection,
      })
    : '';

  const openCompliance = () => {
    setComplianceOpen(true);
    track('compliance_open');
  };

  return (
    <div className="md:flex md:h-full md:flex-col">
      <header className={HEADER.bar}>
        <div className={SHELL.shell}>
        <div className={HEADER.row}>
          <a href="/" className={HEADER.brandWrap}>
            <span className={HEADER.brand}>マプカチ</span>
            <span className={HEADER.tagline}>地点から相場を推定する</span>
          </a>

          <nav className={HEADER.nav}>
            {NAV.map((n) => (
              <a
                key={n.target}
                href={`${n.href}?utm_source=spa&utm_medium=header&utm_campaign=spa_header_${n.target}`}
                onClick={() => track('header_link_click', { target: n.target })}
                className={HEADER.navLinkWide}
              >
                {n.label}
              </a>
            ))}
            {/* The estimator is already the map, so its trailing action is the
                credit route the 不動産情報ライブラリ terms require to be
                reachable — rendered as the same button the other pages use to
                come back here. */}
            <button type="button" onClick={openCompliance} className={SHELL.btnQuiet}>
              免責事項・出典
            </button>
          </nav>
        </div>
        </div>
      </header>

      {/* The panes take the site's measure, so the estimator sits in the same
          column as every other page and the header above it lines up. No
          horizontal padding: the map should reach the column's edge the way a
          sheet does. Past `xl` the form column is pinned to a fixed width and
          the map takes the rest — a form does not get more useful as it gets
          wider, and a map does. */}
      <main className="mx-auto w-full max-w-[1680px] md:grid md:flex-1 md:grid-cols-[1.6fr_1fr] md:grid-rows-[minmax(0,1fr)] md:overflow-hidden xl:grid-cols-[1fr_26rem]">
        <div className="relative isolate h-[38vh] border-b border-rule md:h-full md:min-h-0 md:border-b-0 md:border-r">
          <MapPicker
            position={position}
            onSelect={setPosition}
            priceLabel={result ? `${splitYen(result.predictedPrice).value}${splitYen(result.predictedPrice).unit}` : null}
            loading={loading}
          />

          {/* The chosen point, written out the way a map tool labels its
              current location — a floating chip rather than a panel. */}
          <div className="pointer-events-none absolute bottom-4 left-4 z-[900] rounded bg-sheet/95 px-3.5 py-2.5 shadow-lg backdrop-blur">
            <p className="label text-[10px] text-primary-text">選択地点</p>
            {position ? (
              <>
                <p className="fig text-[13px] leading-snug text-ink">
                  {position[0].toFixed(4)}, {position[1].toFixed(4)}
                </p>
                {result && (
                  <p className="text-[12px] font-bold leading-snug text-ink-2">
                    {result.location.municipalityName}
                    {result.location.districtName ? ` ${result.location.districtName}` : ''}
                  </p>
                )}
              </>
            ) : (
              <p className="text-[12px] font-bold leading-snug text-ink-2">地図をタップ</p>
            )}
          </div>
        </div>

        <div className="bg-sheet md:min-h-0 md:overflow-y-auto">
          <div className="mx-auto max-w-md space-y-9 px-6 py-6 sm:px-10 sm:py-7">
            <PropertyForm
              position={position}
              onSubmit={handleSubmit}
              loading={loading}
              initialValues={formInitial}
              autoSubmit={initial != null}
            />

            <div ref={resultRef} className="scroll-mt-14">
              <ResultPanel
                result={result}
                error={error}
                shareUrl={shareUrl}
                propertyType={lastInput?.values.propertyType ?? 'condo'}
                floorPlan={lastInput?.values.floorPlan ?? '2LDK'}
                buildingYear={lastInput?.values.buildingYear ?? new Date().getFullYear() - 10}
                area={lastInput?.values.area}
              >
                {result && (
                  <SellerTools
                    predictedPrice={result.predictedPrice}
                    municipalityCode={result.location.municipalityCode}
                    municipalityName={result.location.municipalityName}
                    propertyType={lastInput?.values.propertyType ?? 'condo'}
                  />
                )}
              </ResultPanel>
            </div>

            <HistoryPanel history={history} onChange={setHistory} />
            <ExploreLinks />

            {/* Same three statements, in the same order and tone, as
                siteFooter() puts on every generated page: what this service is
                and is not, the 出典 with the processing disclosure, and the
                API terms' credit line verbatim. It used to carry only the
                first, leaving the credit reachable through the modal alone. */}
            <footer className="border-t border-rule pt-5 text-[11px] leading-jp text-ink-3">
              <p>
                マプカチは個人が運営する参考価格の推定ツールです。国土交通省・自治体とは関係ありません。掲載の価格は機械学習による推定値であり、重要事項説明・契約・建築確認などの法定手続には使えません。
              </p>
              <p className="mt-2">
                出典: 国土交通省 不動産情報ライブラリ (
                <a
                  href="https://www.reinfolib.mlit.go.jp/"
                  rel="noreferrer"
                  className={HEADER.navLink}
                >
                  https://www.reinfolib.mlit.go.jp/
                </a>
                ) ・ 国土数値情報 ・ 地理院タイル。マプカチが集計・編集加工しています。
              </p>
              <p className="mt-2">
                このサービスは、国土交通省の不動産情報ライブラリのAPI機能を使用していますが、提供情報の最新性、正確性、完全性等が保証されたものではありません。
              </p>
              <p className="mt-3">
                <button type="button" onClick={openCompliance} className={HEADER.navLink}>
                  免責事項・クレジット
                </button>
                <span className="mx-2 text-rule-strong">/</span>
                <a href="/about.html" className={HEADER.navLink}>
                  マプカチについて
                </a>
                <span className="mx-2 text-rule-strong">/</span>
                <a href="/how-it-works.html" className={HEADER.navLink}>
                  推定の仕組み
                </a>
              </p>
            </footer>
          </div>
        </div>
      </main>

      <ComplianceModal open={complianceOpen} onClose={() => setComplianceOpen(false)} />
    </div>
  );
}
